import { useMemo } from 'react';
import { ROOTS, paths } from 'src/routes/paths';
import { useLocales } from 'src/locales';
import { useGetWorkspacesQuery } from 'src/context/api/workspaces';
import { useAuth } from 'src/auth';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import Iconify from 'src/components/iconify';
import { useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';

const icon = (name: string, flip?: 'horizontal' | 'vertical') => (
  <Iconify icon={name} sx={{ width: 2, height: 2 }} flip={flip} />
);

export function useNavData() {
  const { t } = useLocales();
  const workspaces = useGetWorkspacesQuery(null);
  const { user } = useAuth();
  const workspace = useCurrentWorkspace();
  const demoMode = useSelector((state: any) => state.filters.demoMode);
  const role = user?.role;
  const isLead = workspace?.template && workspace.template.trim().toLowerCase() === 'lead';
  const theme = useTheme();
  const filterStore = useSelector((state: any) => state.filters);
  const isMonoCountry = filterStore?.loadedCountries?.length === 1;

  const data = useMemo(() => {
    const ICONS = {
      brandOverview: icon('cuida:pie-chart-outline'),
      // Optimization Hub
      optimizationHub: icon('fluent:top-speed-20-regular'),
      // Non brand 
      searchTerms: icon('uil:credit-card-search'),
      competitors: icon('lets-icons:user-box'),
      conquestMode: icon('mingcute:target-line'),
      // Brand Pulse
      myCompetitors: icon('hugeicons:sword-01'),
      leaderboard: icon('ph:flag-banner-fold', 'horizontal'),
    };
    const dashboardSection = {
      subheader: 'Brand',
      items: [
        {
          title: 'Brand Overview',
          path: paths.dash.brandOverview,
          icon: ICONS.brandOverview,
        },
        {
          title: 'Optimization Hub',
          path: paths.dash.overview,
          icon: ICONS.optimizationHub,
          activePaths: [
            paths.dash.overview,
            paths.dash.conversionsAnalysis,
            paths.dash.trafficAnalysis,
            paths.dash.campaignExplorer,
            paths.dash.trafficDetails,
            paths.dash.visibilityOverview,
            paths.dash.competitionEvolution,
            paths.dash.competitiveSearchTermAnalysis,
            paths.dash.competitiveAds,
          ],
          children: [
            {
              title: 'Performance',
              path: paths.dash.overview,
              activePaths: [
                paths.dash.overview,
                paths.dash.conversionsAnalysis,
                paths.dash.trafficAnalysis,
                paths.dash.trafficDetails,
              ],
            },
            ...(
              [{
                title: 'Visibility',
                path: paths.dash.visibilityOverview,
                activePaths: [
                  paths.dash.visibilityOverview,
                  paths.dash.competitionEvolution,
                  paths.dash.competitiveSearchTermAnalysis,
                  paths.dash.competitiveAds,
                ],
              }]
            ),
            {
              title: 'Campaigns',
              path: paths.dash.campaignExplorer,
              lastChildren: demoMode || !['admin', 'brand specialist'].includes(role),
            },
            ...(
              !demoMode && ['admin', 'brand specialist'].includes(role)
                ? [{
                  title: 'Activations',
                  path: paths.dash.optimizationActivationsKeywordsExact,
                  lastChildren: true,
                  activePaths: [
                    paths.dash.optimizationActivationsKeywordsExact,
                    paths.dash.optimizationActivationsKeywordsOther,
                    paths.dash.activeMode,
                    paths.dash.koBiddingMaxCPC,
                    paths.dash.koBiddingTROAS,
                    paths.dash.koBiddingTCPA,
                    paths.dash.koBiddingTargetImpressionShare,
                    paths.dash.koBiddingAuto,
                    paths.dash.scraping,
                  ],
                }]
                : []
            ),
          ],
        },

      ],
    };


    const adminSection: any[] = [];


    const brandPulseSection =
      workspace?.brandpulse && (workspace?.brandpulse_visibility?.includes(role))
        ? [{
          subheader: `Brand Pulse${  workspace?.brandpulse_visibility?.includes("customer") ? '' : ' (Internal)'}`,
          items: [
            {
              title: 'My Competitors',
              path: paths.dash.myCompetitors,
              icon: ICONS.myCompetitors,
            },
            {
              title: 'Leaderboard',
              path: paths.dash.leaderboard,
              icon: ICONS.leaderboard,
            }
          ],
        }]
        : [];

    const nonBrandItems = [
      // Search terms si nonbrand vrai
      ...(workspace?.nonbrand && workspace?.nonbrand_visibility?.includes(role)
        ? [
          {
            title: 'Search terms',
            path: paths.dash.nonBrandOverview,
            icon: ICONS.searchTerms,
          },
          {
            title: 'Competitors',
            path: paths.dash.nonBrandCompetitors,
            icon: ICONS.competitors,
          },
        ]
        : []),

      // Conquest Mode si conquestmode vrai
      ...(workspace?.conquestmode && workspace?.conquestmode_visibility?.includes(role)
        ? [
          {
            title: 'Conquest Mode',
            path: paths.dash.conquestMode,
            icon: ICONS.conquestMode,
          },
        ]
        : []),
    ];

    const nonBrandSection = nonBrandItems.length > 0
      ? [
        {
          subheader: `Non Brand${  workspace?.nonbrand_visibility?.includes("customer") ? '' : ' (Internal)'}`,
          items: nonBrandItems,
        },
      ]
      : [];

    return [dashboardSection, ...nonBrandSection, ...brandPulseSection, ...adminSection];
  }, [
    t,
    workspaces.data,
    role,
    workspace?.template,
    isLead,
    demoMode,
    theme.palette.mode,
    isMonoCountry,
    workspace
  ]);

  return data;
}
